import { useMemo } from "react";
import { useSelector } from "react-redux";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { LabelWidthTypes } from "@enums/label-width-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { LayoutTypes } from "@enums/layout-types";
import { ModalTypes } from "@enums/modal-types";
import { RelyingPartyRole } from "@idw-types/common";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { extractRelyingPartiesForPropertyName } from "@utils/permissions-util";
import { getRelyingPartyOptions } from "@utils/validation/organization/organization-users/organization-users-util";
import { LabelValueContainer } from "@containers/common/label-value-container/label-value-container";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { AddRelyingPartyRoleModal } from "@containers/modals/add-relying-party-role-modal/add-relying-party-role-modal";
import styles from "./relying-party-roles-container.less";
import { getSessionState } from "@redux/selectors/root-selectors";

type RelyingPartyRolesContainerProps = {
    isDisabled: boolean;
    requiredUserPermissionType: UserPermissionTypes;
    sessionUserHasAccessToAllAdminUserRPs: boolean;
    relyingPartyRolesWithPermissions: Array<{
        relyingPartyRole: RelyingPartyRole;
        canView: boolean;
        canModify: boolean;
    }>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
    axnAdminUserPropertyBundleOptions: Array<SelectOption>;
    labelWidth: LabelWidthTypes;
    addRelyingPartyRole: (relyingPartyRole: RelyingPartyRole) => void;
    removeRelyingPartyRole: (relyingPartyRole: RelyingPartyRole) => void;
    updateRelyingPartyRole: (relyingPartyRole: RelyingPartyRole) => void;
};

export const RelyingPartyRolesContainer = (props: RelyingPartyRolesContainerProps) => {
    const {
        isDisabled,
        requiredUserPermissionType,
        sessionUserHasAccessToAllAdminUserRPs,
        relyingPartyRolesWithPermissions,
        axnRelyingParties,
        axnAdminUserPropertyBundles,
        axnAdminUserPropertyBundleOptions,
        labelWidth,
        addRelyingPartyRole,
        removeRelyingPartyRole,
        updateRelyingPartyRole
    } = props;

    const { session } = useSelector(getSessionState);

    const modalContext = useModalContext();

    const handleRemoveRelyingPartyRole = (oldRelyingPartyId: string) => {
        const oldRelyingPartyRole = relyingPartyRolesWithPermissions.find(rpr => rpr.relyingPartyRole.relyingParty.id === oldRelyingPartyId);
        if (oldRelyingPartyRole) {
            removeRelyingPartyRole(oldRelyingPartyRole.relyingPartyRole);
        }
    };

    const handleAddRelyingPartyRole = (relyingPartyId: string, propertyBundleShortName: string) => {
        const newRelyingParty = axnRelyingParties.find(rp => rp.id === relyingPartyId);
        const newAxnAdminUserPropertyBundle = axnAdminUserPropertyBundles.find(bundle => bundle.shortName === propertyBundleShortName);

        if (newRelyingParty && newAxnAdminUserPropertyBundle) {
            addRelyingPartyRole({
                relyingParty: newRelyingParty,
                bundle: newAxnAdminUserPropertyBundle
            });
        }
    };

    const handleUpdateRelyingPartyRole = (relyingPartyId: string, newBundle: SelectOption) => {
        const newAxnAdminUserPropertyBundle = axnAdminUserPropertyBundles.find(bundle => bundle.shortName === newBundle.value);
        const oldRelyingPartyRole = relyingPartyRolesWithPermissions.find(rpr => rpr.relyingPartyRole.relyingParty.id === relyingPartyId);

        if (oldRelyingPartyRole && newAxnAdminUserPropertyBundle) {
            updateRelyingPartyRole({
                relyingParty: oldRelyingPartyRole.relyingPartyRole.relyingParty,
                bundle: newAxnAdminUserPropertyBundle
            });
        }
    };

    const axnRelyingPartyOptions = useMemo(() => {
        const finalRelyingParties = extractRelyingPartiesForPropertyName(
            requiredUserPermissionType,
            session.user.axnAdminUserRPProperties,
            axnRelyingParties
        );
        return getRelyingPartyOptions(finalRelyingParties, null);
    }, [axnRelyingParties, null]);

    const unselectedRelyingPartyOptions = useMemo(() => {
        const selectedRpIds = relyingPartyRolesWithPermissions.map(pr => pr.relyingPartyRole.relyingParty.id);
        return axnRelyingPartyOptions.filter(option => !selectedRpIds.includes(option.value));
    }, [axnRelyingPartyOptions, relyingPartyRolesWithPermissions]);

    const onClickAddRelyingPartyRole = () => {
        const renderAddRelyingPartyRoleModal = (onModalClose: () => void) => {
            return (
                <AddRelyingPartyRoleModal
                    onModalClose={onModalClose}
                    addRelyingPartyRole={handleAddRelyingPartyRole}
                    relyingPartyOptions={unselectedRelyingPartyOptions}
                    userPropertyBundleOptions={axnAdminUserPropertyBundleOptions}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.AddRelyingPartyRole,
            renderFunction: renderAddRelyingPartyRoleModal
        })
    };

    const detailsResources = resources.pages.organizationUserDetails.tabs.details;

    return (
        <div className={styles.relyingPartyRolesContainer}>
            <div className={styles.relyingPartyRolesHeader}>
                <LabelValueContainer
                    isRequired={true}
                    controlId={`rp-user-label-value`}
                    labelIsBold={false}
                    layout={LayoutTypes.ROW}
                    value={detailsResources.inputs.relyingPartyRoles.description}
                    label={detailsResources.inputs.relyingPartyRoles.label}
                    labelWidth={labelWidth}
                />
                <Button
                    id="add-relying-party-role-button"
                    disabled={isDisabled || unselectedRelyingPartyOptions.length === 0}
                    onClick={onClickAddRelyingPartyRole}
                >
                    {detailsResources.buttons.addRelyingPartyRole.label}
                </Button>
            </div>
            {!sessionUserHasAccessToAllAdminUserRPs && (
                <Alert variant={"warning"}>{detailsResources.accessMessage}</Alert>
            )}
            {relyingPartyRolesWithPermissions.filter(rpr => rpr.canView).length > 0 && (
                <div className={styles.relyingPartyRolesContent}>
                    {relyingPartyRolesWithPermissions.filter(rpr => rpr.canView).map(selectedRp => (
                        <div className={styles.relyingPartyRoleContainer} key={`user-relying-party-${selectedRp.relyingPartyRole.relyingParty.id}`}>
                            <LabelValueContainer
                                controlId={`user-relying-party-${selectedRp.relyingPartyRole.relyingParty.id}-rpname`}
                                label={detailsResources.inputs.relyingParty.label}
                                labelIsBold={false}
                                labelWidth={LabelWidthTypes.SMALL}
                                layout={LayoutTypes.ROW}
                                isRequired={true}
                                value={selectedRp.relyingPartyRole.relyingParty.name}
                            />
                            <SelectFormGroup
                                controlId={`user-relying-party-${selectedRp.relyingPartyRole.relyingParty.id}-rprole`}
                                className={styles.relyingPartyRoleSelect}
                                ariaLabel={detailsResources.inputs.role.label}
                                automationId={`user-relying-party-${selectedRp.relyingPartyRole.relyingParty.id}-rprole`}
                                isClearable={false}
                                isSearchable={false}
                                labelWidth={LabelWidthTypes.EXTRA_SMALL}
                                options={axnAdminUserPropertyBundleOptions}
                                selectedOption={
                                    selectedRp.relyingPartyRole.bundle
                                        ? { label: selectedRp.relyingPartyRole.bundle.name, value: selectedRp.relyingPartyRole.bundle.shortName }
                                        : { label: "Custom", value: null }
                                }
                                disabled={isDisabled || !selectedRp.canModify}
                                isRequired={true}
                                label={detailsResources.inputs.role.label}
                                onChange={(newValue: SelectOption) => handleUpdateRelyingPartyRole(selectedRp.relyingPartyRole.relyingParty.id, newValue)}
                                afterInputIcon={(isDisabled || !selectedRp.canModify) ? null : faTrash}
                                afterInputIconDisabled={isDisabled || !selectedRp.canModify}
                                afterInputIconOnClick={() => handleRemoveRelyingPartyRole(selectedRp.relyingPartyRole.relyingParty.id)}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};